import { produce } from 'immer';
import { atom, selector } from 'recoil';

import { vehiclesState } from '@/states/used-cars/vehicles.state';
import { CarFilter } from '@/types/filters/filters.types';
import { guardRecoilDefaultValue } from '@/utils/atom';

export const colorFilterState = atom<CarFilter[]>({
  key: 'colorFilterState',
  default: [],
});

export const colorFilterSearchOptionsSelector = selector({
  key: 'colorFilterSearchOptionsSelector',
  get: ({ get }) => {
    const colourTypes = get(colorFilterState);
    const colourTypeSearchOptions: CarFilter[] = colourTypes.filter(
      (option) => !option.isSelected,
    );

    return { colourTypeSearchOptions };
  },
});

export const toggleColor = selector<number | null>({
  key: 'toggleColor',
  get: () => null,
  set: ({ set, get }, colourTypeId) => {
    const colourTypes = get(colorFilterState);

    const updatedColourType = produce(colourTypes, (colourTypesDraft) => {
      if (colourTypeId) {
        const colourType = colourTypesDraft.find(
          (colourType) => colourType.id === colourTypeId,
        );

        colourType!.isSelected = !colourType?.isSelected;
        return;
      }
    });

    set(colorFilterState, updatedColourType);
  },
});

export const selectedColorFiltersSelector = selector<CarFilter[]>({
  key: 'selectedColorFiltersSelector',
  get: ({ get }) => {
    const colourTypes = get(colorFilterState);

    return colourTypes.filter((colourType) => colourType.isSelected);
  },
});

export const visibleColorFiltersSelector = selector<CarFilter[]>({
  key: 'visibleColorFiltersSelector',
  get: ({ get }) => {
    const colourTypes = get(colorFilterState);
    const vehicles = get(vehiclesState);

    const bodyColorsId = [
      ...new Set(vehicles.map((vehicle) => vehicle.bodyColorId)),
    ];

    return colourTypes.map((colourType) => ({
      ...colourType,
      isDisabled: !bodyColorsId.includes(colourType.id),
    }));
  },
});

export const hasSelectedColorFiltersSelector = selector<boolean>({
  key: 'hasSelectedColorFiltersSelector',
  get: ({ get }) => {
    return Boolean(get(selectedColorFiltersSelector).length);
  },
});

export const resetSelectedColorFiltersSelector = selector<number | null>({
  key: 'resetSelectedColorFiltersSelector',
  get: () => null,
  set: ({ set, get }, colourTypeId) => {
    const colourTypes = get(colorFilterState);

    const updatedColourType = produce(colourTypes, (colourTypesDraft) => {
      if (colourTypeId) {
        const colourType = colourTypesDraft.find(
          (colourType) => colourType.id === colourTypeId,
        );

        colourType!.isSelected = false;
        return;
      }

      colourTypesDraft.forEach((colourType) => {
        if (colourTypeId && colourType.id === colourTypeId) {
          colourType.isSelected = false;
          return;
        }
        colourType.isSelected = false;
      });
    });

    set(colorFilterState, updatedColourType);
  },
});

export const selectColor = selector({
  key: 'selectColor',
  get: () => [],
  set: ({ set, get }, colorTypeIds) => {
    const colorTypes = get(colorFilterState);

    const updatedColorType = produce(colorTypes, (colorTypesDraft) => {
      if (guardRecoilDefaultValue(colorTypeIds)) return;

      if (colorTypeIds.length) {
        colorTypeIds
          .map((colorTypeId) => Number(colorTypeId))
          .forEach((colorTypeId) => {
            const colorType = colorTypesDraft.find(
              (colorType) => colorType.id === colorTypeId,
            );

            if (!colorType) return;
            colorType.isSelected = true;
          });

        return;
      }
    });

    set(colorFilterState, updatedColorType);
  },
});
